package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.analytics.AnalyticsTracker
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.notifications.LocalToastState
import com.arena_web.ui.components.widgets.CoinIcon
import com.arena_web.ui.components.widgets.InlineSpinner
import com.arena_web.ui.components.widgets.formatCoins
import com.arena_web.ui.localization.Language
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.localization.LocalLanguageState
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Img
import kotlin.math.round

private const val DIALOG_BG = "#09142d"
private const val CARD_BORDER = "#3257ad"

data class CoinPlan(
    val id: String,
    val title: String,
    val coinAmount: Int,
    /** Display price, e.g. "4.99" — must match the backend CoinPlan cents. */
    val eurPrice: String,
    val stackAsset: String,
)

/**
 * Display-only EUR→UAH rate for Ukrainian-locale users. mono still invoices the
 * exact EUR amount — the shown ₴ price is an approximation of what the bank
 * will charge; bump the constant when the rate drifts.
 */
private const val UAH_PER_EUR = 49.0

/**
 * Coin-pack price as shown to the user: "€4.99" everywhere, "245 ₴" when the
 * app language is Ukrainian.
 */
@Composable
internal fun formatPlanPrice(eurPrice: String): String {
    val isUkrainian = LocalLanguageState.current.currentLanguage == Language.UKRAINIAN
    val eur = eurPrice.toDoubleOrNull()
    if (!isUkrainian || eur == null) return "€$eurPrice"
    return "${round(eur * UAH_PER_EUR).toInt()} ₴"
}

internal val DEFAULT_PLANS = listOf(
    CoinPlan(
        id = "starter",
        title = "Starter pack",
        coinAmount = 100,
        eurPrice = "4.99",
        stackAsset = "/images/coins/stack-starter.png",
    ),
    CoinPlan(
        id = "popular",
        title = "Popular pack",
        coinAmount = 500,
        eurPrice = "19.99",
        stackAsset = "/images/coins/stack-popular.png",
    ),
    CoinPlan(
        id = "premium",
        title = "Premium pack",
        coinAmount = 1500,
        eurPrice = "49.99",
        stackAsset = "/images/coins/stack-premium.png",
    ),
)

@Composable
fun BuyCoinsDialog(
    isOpen: Boolean,
    onClose: () -> Unit,
    walletBalance: String?,
    plans: List<CoinPlan> = DEFAULT_PLANS,
    /** Stacking order. Defaults to 1010 (above the app shell); callers that open
     *  it on top of a mobile BottomSheet (z-index 1100) pass a higher value. */
    zIndex: Int = 1010,
) {
    if (!isOpen) return

    val container = LocalAppContainer.current
    val toast = LocalToastState.current
    val scope = rememberCoroutineScope()
    val walletRepo = container.walletRepository
    val t = LocalAppStrings.current

    // Plan id of the in-flight purchase; null when idle. Disables every Buy
    // button so the user can't fire two purchases at once or close mid-flight.
    var purchasingId by remember(isOpen) { mutableStateOf<String?>(null) }
    val isPurchasing = purchasingId != null

    // Selecting a pack starts a mono checkout: the tab is redirected to the
    // hosted payment page and the result is handled on the next app load by
    // resumePendingTopUp in AppEntry. Coins are credited by the server-side
    // webhook regardless.
    val onPlanSelected: (CoinPlan) -> Unit = onPlanSelected@{ plan ->
        if (isPurchasing) return@onPlanSelected
        val repo = walletRepo ?: run {
            toast.error(t.walletSignInToBuy)
            return@onPlanSelected
        }
        purchasingId = plan.id
        AnalyticsTracker.trackClick(
            "wallet_topup",
            params = mapOf(
                "plan_id" to plan.id,
                "amount_eur" to plan.eurPrice,
                "method" to "web",
            ),
        )
        startMonoPurchase(
            scope = scope,
            repository = repo,
            planId = plan.id,
            onFailed = { message ->
                purchasingId = null
                toast.error(message ?: t.walletPurchaseFailed)
            },
        )
    }

    if (isMobile()) {
        BuyCoinsSheet(
            walletBalance = walletBalance,
            plans = plans,
            purchasingId = purchasingId,
            onPlanSelected = onPlanSelected,
            onClose = { if (!isPurchasing) onClose() },
        )
        return
    }

    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(zIndex)
            .styleModifier { property("background", "rgba(0, 0, 0, 0.65)") }
            .onClick { if (!isPurchasing) onClose() },
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .sMaxWidth(1574)
                .fillMaxWidth()
                .sMargin(16)
                .sBorderRadius(26)
                .styleModifier {
                    property("padding", "${s(22)} ${s(22)} ${s(40)}")
                    property("background", DIALOG_BG)
                    property("overflow", "hidden")
                    property("box-sizing", "border-box")
                }
                .sGap(40)
                .onClick { it.stopPropagation() },
        ) {
            DialogHeader(
                walletBalance = walletBalance,
                onClose = { if (!isPurchasing) onClose() },
            )
            PlanCardsRow(
                plans = plans,
                purchasingId = purchasingId,
                onPlanSelected = onPlanSelected,
            )
        }
    }
}

// =============================================================================
// Mobile — "My Wallet" as a bottom sheet. The tall premium coin cards keep
// their full design but render in a horizontal swipe carousel (scaled to the
// phone via a local --scale override) so the visual identity is preserved.
// =============================================================================
@Composable
private fun BuyCoinsSheet(
    walletBalance: String?,
    plans: List<CoinPlan>,
    purchasingId: String?,
    onPlanSelected: (CoinPlan) -> Unit,
    onClose: () -> Unit,
) {
    BottomSheet(onDismiss = onClose, contentGap = 16) {
        Column(modifier = Modifier.fillMaxWidth().sGap(16)) {
            // Header — wallet icon + title on the left, balance on the right.
            Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
                Row(
                    modifier = Modifier.fillMaxWidth().sGap(8),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Icon(src = AppIcons.Wallet, modifier = Modifier.sSize(24))
                    SpanText(
                        LocalAppStrings.current.walletTitle,
                        modifier = Modifier
                            .flexGrow(1)
                            .sFontSize(20)
                            .fontWeight(AppTypography.Medium)
                            .sLineHeight(26)
                            .sLetterSpacing(0.2)
                            .color(AppColors.Text.Secondary),
                    )
                    CoinIcon(22)
                    SpanText(
                        "× ${formatCoins(walletBalance ?: "0")}",
                        modifier = Modifier
                            .sFontSize(18)
                            .fontWeight(AppTypography.Bold)
                            .sLineHeight(22)
                            .sLetterSpacing(0.09)
                            .color(LocalSportColors.current.primary),
                    )
                    SpanText(
                        LocalAppStrings.current.walletCredits,
                        modifier = Modifier
                            .sFontSize(14)
                            .fontWeight(AppTypography.Regular)
                            .sLetterSpacing(0.16)
                            .color(AppColors.Text.Secondary),
                    )
                }
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(1)
                        .styleModifier {
                            property(
                                "background",
                                "linear-gradient(90deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)",
                            )
                        },
                )
            }

            // Vertical list of packs — one compact row each, keeping the
            // premium dark/blue card styling but laid out for the phone.
            Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
                plans.forEach { plan ->
                    PlanRow(
                        plan = plan,
                        purchasing = purchasingId == plan.id,
                        disabled = purchasingId != null && purchasingId != plan.id,
                        onClick = { onPlanSelected(plan) },
                    )
                }
            }
        }
    }
}

// Compact pack row for the mobile wallet list — coin-stack thumbnail, title +
// amount, and a price pill on the trailing edge.
@Composable
private fun PlanRow(
    plan: CoinPlan,
    purchasing: Boolean,
    disabled: Boolean,
    onClick: () -> Unit,
) {
    val sportColors = LocalSportColors.current
    val interactive = !purchasing && !disabled
    // The whole row is the tap target; the price pill inside is visual only.
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sGap(14)
            .sBorderRadius(20)
            .then(
                if (interactive) Modifier.cursor(Cursor.Pointer).onClick { onClick() }
                else Modifier.cursor(Cursor.NotAllowed),
            )
            .styleModifier {
                property("background", "linear-gradient(180deg, #0d1119, #000000)")
                property("border", "0.5px solid $CARD_BORDER")
                property("box-sizing", "border-box")
                property("padding", "${s(12)} ${s(16)}")
                property("box-shadow", "inset 0px -40px 70px -34px #114cce")
                if (disabled) property("opacity", "0.5")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Img(
            src = plan.stackAsset,
            attrs = {
                attr("alt", "")
                attr("aria-hidden", "true")
                attr("draggable", "false")
                style {
                    property("width", s(64))
                    property("height", s(56))
                    property("object-fit", "contain")
                    property("flex-shrink", "0")
                    property("pointer-events", "none")
                }
            },
        )
        Column(modifier = Modifier.flexGrow(1).sGap(4)) {
            SpanText(
                plan.title,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(20)
                    .sLetterSpacing(0.032)
                    .color(AppColors.Text.Secondary),
            )
            Row(modifier = Modifier.sGap(6), verticalAlignment = Alignment.CenterVertically) {
                CoinIcon(24)
                SpanText(
                    "× ${plan.coinAmount}",
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.SemiBold)
                        .sLineHeight(20)
                        .sLetterSpacing(0.032)
                        .color(AppColors.Text.Secondary),
                )
            }
        }
        Box(
            modifier = Modifier
                .sWidth(84)
                .sHeight(40)
                .sBorderRadius(100)
                .styleModifier {
                    property("flex-shrink", "0")
                    property(
                        "background",
                        sportColors.primary.toRgb().let { "rgb(${it.red}, ${it.green}, ${it.blue})" },
                    )
                    if (!interactive) property("opacity", if (purchasing) "0.85" else "0.5")
                },
            contentAlignment = Alignment.Center,
        ) {
            if (purchasing) {
                // Brand loader recolored to the button's onPrimary (it's natively
                // blue, which would vanish on the blue fill).
                Box(modifier = Modifier.styleModifier { property("filter", "brightness(0) invert(1)") }) {
                    InlineSpinner(size = 22.px)
                }
            } else {
                SpanText(
                    formatPlanPrice(plan.eurPrice),
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(18)
                        .sLetterSpacing(0.032)
                        .color(sportColors.onPrimary),
                )
            }
        }
    }
}

@Composable
private fun DialogHeader(walletBalance: String?, onClose: () -> Unit) {
    val t = LocalAppStrings.current
    Column(modifier = Modifier.fillMaxWidth().sGap(0)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Row(
                modifier = Modifier.sGap(22),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Row(
                    modifier = Modifier.sGap(12),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Icon(src = AppIcons.Wallet, modifier = Modifier.sSize(24))
                    SpanText(
                        t.walletTitle,
                        modifier = Modifier
                            .sFontSize(24)
                            .fontWeight(AppTypography.Medium)
                            .sLineHeight(32)
                            .sLetterSpacing(0.24)
                            .color(AppColors.Text.Secondary),
                    )
                }
                Box(
                    modifier = Modifier
                        .sWidth(1)
                        .sHeight(26)
                        .styleModifier {
                            property("background", "rgba(148, 149, 152, 0.4)")
                        },
                )
                Row(
                    modifier = Modifier.sGap(6),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    CoinIcon(24)
                    SpanText(
                        "× ${formatCoins(walletBalance ?: "0")}",
                        modifier = Modifier
                            .sFontSize(18)
                            .fontWeight(AppTypography.Bold)
                            .sLineHeight(22)
                            .sLetterSpacing(0.09)
                            .color(LocalSportColors.current.primary),
                    )
                    SpanText(
                        t.walletCredits,
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.Regular)
                            .sLetterSpacing(0.16)
                            .color(AppColors.Text.Secondary),
                    )
                }
            }
            CloseButton(onClose = onClose)
        }
        Box(modifier = Modifier.sHeight(12))
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(1)
                .styleModifier {
                    property(
                        "background",
                        "linear-gradient(90deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)",
                    )
                },
        )
    }
}

@Composable
private fun CloseButton(onClose: () -> Unit) {
    Box(
        modifier = Modifier
            .sSize(44)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClose() }
            .styleModifier {
                property("position", "relative")
                property(
                    "background",
                    "linear-gradient(24deg, rgba(0, 59, 174, 0.08) 27%, rgba(3, 87, 230, 0.08) 68%)",
                )
                property("border", "0.3px solid rgba(255, 255, 255, 0.4)")
                property("box-sizing", "border-box")
            },
        contentAlignment = Alignment.Center,
    ) {
        Icon(src = AppIcons.Close, modifier = Modifier.sSize(16))
    }
}

@Composable
private fun PlanCardsRow(
    plans: List<CoinPlan>,
    purchasingId: String?,
    onPlanSelected: (CoinPlan) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().sGap(22),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.Top,
    ) {
        plans.forEach { plan ->
            PlanCard(
                plan = plan,
                purchasing = purchasingId == plan.id,
                disabled = purchasingId != null && purchasingId != plan.id,
                onClick = { onPlanSelected(plan) },
            )
        }
    }
}

@Composable
private fun PlanCard(
    plan: CoinPlan,
    purchasing: Boolean,
    disabled: Boolean,
    onClick: () -> Unit,
) {
    // The whole card is the click target; the Buy button inside is visual only.
    val interactive = !purchasing && !disabled
    Box(
        modifier = Modifier
            .sWidth(472)
            .sHeight(640)
            .sBorderRadius(40)
            .then(
                if (interactive) Modifier.cursor(Cursor.Pointer).onClick { onClick() }
                else Modifier.cursor(Cursor.NotAllowed),
            )
            .styleModifier {
                property("position", "relative")
                property("background", "linear-gradient(180deg, #0d1119, #000000)")
                property("border", "0.5px solid $CARD_BORDER")
                property("box-sizing", "border-box")
                property("overflow", "hidden")
                property(
                    "box-shadow",
                    listOf(
                        "inset 0px 8px 10px -2px rgba(48, 100, 222, 0.7)",
                        "inset 0px -20px 20px -10px rgba(255, 255, 255, 0.1)",
                        "inset 0px -15px 50px -8px rgba(101, 148, 254, 0.7)",
                        "inset 0px -80px 150px -30px #114cce",
                    ).joinToString(", "),
                )
            },
    ) {
        Img(
            src = plan.stackAsset,
            attrs = {
                attr("alt", "")
                attr("aria-hidden", "true")
                attr("draggable", "false")
                style {
                    property("position", "absolute")
                    property("left", "50%")
                    property("top", "${s(200)}")
                    property("transform", "translateX(-50%)")
                    property("width", s(412))
                    property("height", s(332))
                    property("object-fit", "contain")
                    property("pointer-events", "none")
                }
            },
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("padding", "${s(25)} ${s(30)} ${s(59)}")
                    property("box-sizing", "border-box")
                    property("position", "relative")
                    property("z-index", "1")
                },
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                plan.title,
                modifier = Modifier
                    .sFontSize(28)
                    .fontWeight(AppTypography.SemiBold)
                    .sLineHeight(46)
                    .sLetterSpacing(1.12)
                    .color(AppColors.Text.Secondary),
            )
            Box(modifier = Modifier.sHeight(18))
            PriceRow(coinAmount = plan.coinAmount)
            Box(
                modifier = Modifier.styleModifier { property("flex", "1 1 auto") },
            )
            BuyButton(
                eurPrice = plan.eurPrice,
                purchasing = purchasing,
                disabled = disabled,
            )
        }
    }
}

@Composable
private fun PriceRow(coinAmount: Int) {
    Row(
        modifier = Modifier.sGap(9),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Img(
            src = "/images/coins/coin.png",
            attrs = {
                attr("alt", "")
                attr("draggable", "false")
                style {
                    property("width", s(64))
                    property("height", s(67))
                    property("object-fit", "contain")
                    property("display", "block")
                }
            },
        )
        SpanText(
            "×",
            modifier = Modifier
                .sFontSize(30)
                .fontWeight(AppTypography.Medium)
                .color(AppColors.Text.Secondary),
        )
        SpanText(
            coinAmount.toString(),
            modifier = Modifier
                .sFontSize(34)
                .fontWeight(AppTypography.SemiBold)
                .sLineHeight(40)
                .sLetterSpacing(0.68)
                .color(AppColors.Text.Secondary),
        )
    }
}

@Composable
// Visual only — the enclosing PlanCard owns the click (whole card is tappable).
private fun BuyButton(
    eurPrice: String,
    purchasing: Boolean,
    disabled: Boolean,
) {
    val sportColors = LocalSportColors.current
    val interactive = !purchasing && !disabled
    Box(
        modifier = Modifier
            .sWidth(260)
            .sHeight(56)
            .sBorderRadius(100)
            .styleModifier {
                property(
                    "background",
                    sportColors.primary.toRgb().let {
                        "rgb(${it.red}, ${it.green}, ${it.blue})"
                    },
                )
                if (!interactive) property("opacity", if (purchasing) "0.85" else "0.5")
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            if (purchasing) LocalAppStrings.current.walletProcessing
            else LocalAppStrings.current.walletBuyFor(formatPlanPrice(eurPrice)),
            modifier = Modifier
                .sFontSize(22)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.044)
                .color(sportColors.onPrimary),
        )
    }
}
